import React, { useCallback, useEffect, useState } from 'react';
import { Network, CalendarPlus, ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { Cohort, CohortSuggestion } from '../../../types/aai';
import { Loading, ErrorNote } from '../components';
import { Card, MonoLabel } from '../dashboards/ui';

// Trainer "Cohorts" (design's cohort builder) — group people into cohorts. Orbit
// suggests cuts (by team / availability / readiness / tool-usage) you'd never find
// by hand; create one, then generate its weekly plan. Sessions live on Delivery.
const CohortsPage: React.FC = () => {
  const [suggestions, setSuggestions] = useState<CohortSuggestion[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // cohort expansion — members fetched on first open, then cached
  const [openCohort, setOpenCohort] = useState<string | null>(null);
  const [cohortMembers, setCohortMembers] = useState<Record<string, string[]>>({});
  const [genNotice, setGenNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([aaiApi.suggestCohorts(), aaiApi.listCohorts()]);
      setSuggestions(s.data.suggestions); setCohorts(c.data.cohorts); setError(null);
    } catch { setError('Could not load cohorts.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createFromSuggestion = async (sg: CohortSuggestion) => {
    setBusy(true);
    try { await aaiApi.createCohort({ name: sg.name, cut_type: sg.cut_type, criteria: sg.criteria, member_emails: sg.members }); await load(); }
    finally { setBusy(false); }
  };

  const toggleCohort = async (id: string) => {
    if (openCohort === id) { setOpenCohort(null); return; }
    setOpenCohort(id);
    if (!cohortMembers[id]) {
      try {
        const { data } = await aaiApi.getCohort(id);
        setCohortMembers((m) => ({ ...m, [id]: data.members }));
      } catch { /* row simply shows no members */ }
    }
  };

  const removeCohort = async (id: string) => {
    setBusy(true);
    try { await aaiApi.deleteCohort(id); if (openCohort === id) setOpenCohort(null); await load(); }
    finally { setBusy(false); }
  };

  const generatePlan = async (cohortId: string) => {
    setBusy(true);
    try {
      const { data } = await aaiApi.generateSessions(cohortId);
      setGenNotice(`Weekly plan: ${data.created} session${data.created === 1 ? '' : 's'} added${data.skipped ? `, ${data.skipped} already scheduled` : ''}. Find them on the Delivery tab.`);
      await load();
    } finally { setBusy(false); }
  };

  if (loading) return <Loading label="Loading cohorts…" />;
  if (error) return <ErrorNote message={error} />;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-2 mb-1"><Network size={22} className="text-brand-500" /><h1 className="text-2xl font-semibold text-gray-900">Cohorts</h1></div>
      <p className="text-sm text-gray-500 mb-6">Group people into cohorts — Orbit suggests cuts (by team, availability, readiness, or tool-usage) you'd never find by hand. Create one, then generate its weekly plan.</p>

      {/* suggested cuts (Orbit) */}
      <Card className="mb-4">
        <MonoLabel className="mb-3">Suggested cohorts</MonoLabel>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((sg, i) => (
            <button key={i} disabled={busy} onClick={() => createFromSuggestion(sg)}
              className="bg-white border border-gray-200 rounded-full pl-3 pr-2 py-1 text-[12.5px] hover:border-brand-300 disabled:opacity-50 flex items-center gap-1.5">
              <span className="font-sans text-[10px] uppercase text-gray-400">{sg.cut_type}</span>
              <span className="font-medium text-gray-700">{sg.name}</span>
              <span className="bg-brand-50 text-brand-600 rounded-full w-5 h-5 grid place-items-center text-[11px] font-bold">+</span>
            </button>
          ))}
          {suggestions.length === 0 && (
            <p className="text-[13px] text-gray-400">
              {cohorts.length > 0
                ? 'No new suggestions — every suggested group already has a cohort.'
                : 'No suggestions yet — enroll and onboard people first.'}
            </p>
          )}
        </div>
      </Card>

      {/* cohorts */}
      <Card className="mb-4">
        <MonoLabel className="mb-3">Cohorts</MonoLabel>
        {genNotice && <p className="mb-2 text-[12px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{genNotice}</p>}
        {cohorts.length === 0 ? <p className="text-[13px] text-gray-400">No cohorts yet — create one from a suggestion above.</p> : (
          <div className="space-y-1.5">
            {cohorts.map((c) => (
              <div key={c.id} className="bg-white rounded-lg px-3 py-2">
                <div className="flex items-center gap-2.5">
                  <button onClick={() => toggleCohort(c.id)} title={openCohort === c.id ? 'Hide members' : 'Show members'}
                    className="text-gray-400 hover:text-gray-700">
                    {openCohort === c.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                  </button>
                  <span className="text-[13px] font-medium flex-1">{c.name}</span>
                  <span className="text-[10.5px] font-sans uppercase text-gray-400">{c.cut_type}</span>
                  <span className="text-[11.5px] text-gray-500">{c.size} {c.size === 1 ? 'person' : 'people'}</span>
                  <button disabled={busy} onClick={() => generatePlan(c.id)} title="Generate the weekly session plan from the program calendar"
                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-brand-600 hover:text-brand-700 disabled:opacity-50"><CalendarPlus size={13} /> Weekly plan</button>
                  <button disabled={busy} onClick={() => removeCohort(c.id)} title="Remove cohort (past sessions keep their history)"
                    className="text-gray-300 hover:text-red-500 disabled:opacity-50"><Trash2 size={14} /></button>
                </div>
                {openCohort === c.id && (
                  <div className="mt-2 ml-6 border-l-2 border-gray-100 pl-3">
                    {(cohortMembers[c.id] || []).map((m) => (
                      <div key={m} className="text-[12.5px] text-gray-600 py-0.5">{m}</div>
                    ))}
                    {(cohortMembers[c.id] || []).length === 0 && <p className="text-[12px] text-gray-400 py-0.5">No members in this cohort.</p>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default CohortsPage;
